import React, { useEffect, useState } from "react";
import ContentPostsPromProfiles from "./ContentPostsPromProfiles";
import { FaStar } from "react-icons/fa";
import { doc, collection, onSnapshot, setDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../hooks/useAuth"; // Asegúrate que exista este hook
import FirstMessageToPromotionalModal from "./FirstMessageToPromotionalModal";


function PromotionalPublicProfileModal({ user, promoProfileId, onClose }) {
  const [stars, setStars] = useState([]);
  const [hasStar, setHasStar] = useState(false);
  const [processingStar, setProcessingStar] = useState(false);
  const currentUser = useAuth();
  const [open, setOpen] = useState(false);
  // Escuchar cambios en estrellas
  useEffect(() => {
    if (!user?.uid || !promoProfileId) return;

    const starsRef = collection(
      db,
      "profiles",
      "StarsPromocionalProfiles",
      user.uid,
      promoProfileId,
      "stars"
    );

    const unsubscribe = onSnapshot(starsRef, (snapshot) => {
      const starIds = snapshot.docs.map((doc) => doc.id);
      setStars(starIds);
      setHasStar(starIds.includes(currentUser?.uid));
    });

    return () => unsubscribe();
  }, [user?.uid, promoProfileId, currentUser?.uid]);


  const toggleStar = async () => {
    if (!currentUser?.uid || !user?.uid || currentUser.uid === user.uid || !promoProfileId) return;
    setProcessingStar(true);

    const starRef = doc(
      db,
      "profiles",
      "StarsPromocionalProfiles",
      user.uid,
      promoProfileId,
      "stars",
      currentUser.uid
    );

    try {
      if (hasStar) {
        await deleteDoc(starRef);
      } else {
        await setDoc(starRef, {
          uid: currentUser.uid,
          timestamp: Date.now(),
        });
      }
    } catch (err) {
      console.error("Error actualizando estrella:", err);
    } finally {
      setProcessingStar(false);
    }
  };



  if (!user) return null;

  return (
    <>
      {/* Fondo borroso */}
      <div
        className="fixed inset-0 z-[9998] bg-black bg-opacity-30 backdrop-blur-sm"
        onClick={onClose} // opcional: cerrar modal al clicar fondo
      ></div>

      {/* Modal */}
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-lg max-w-md w-full p-6 relative max-h-[80vh] overflow-auto">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-yellow-700 dark:text-yellow-300 hover:text-yellow-900 dark:hover:text-yellow-100 text-xl font-bold"
            aria-label="Cerrar modal"
          >
            ×
          </button>

        <div className="flex flex-col items-center gap-4 px-4 py-6 max-w-md mx-auto">
          <img
            src={user.profilePhotoUrl}
            alt={user.name}
            className="w-24 h-24 rounded-full object-cover border-4 border-yellow-500"
          />

          <h2
            id="modal-title"
            className="text-2xl font-bold text-yellow-900 dark:text-yellow-100 text-center"
          >
            {user.name}
          </h2>

          {/* Descripción */}
          <p
            id="modal-description"
            className="text-yellow-700 dark:text-yellow-300 text-center px-2"
          >
            {user.description || "Perfil público sin descripción."}
          </p>

          {/* Tags */}
          <div className="flex flex-wrap justify-center gap-2 max-w-xs">
            {user.tags && user.tags.length > 0 ? (
              user.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="bg-yellow-100 dark:bg-yellow-700 text-yellow-800 dark:text-yellow-100 text-sm font-semibold px-3 py-1 rounded-full select-none whitespace-nowrap"
                >
                  {tag}
                </span>
              ))
            ) : (
              <p className="text-yellow-500 dark:text-yellow-400 text-sm italic">Sin tags</p>
            )}
          </div>

          {/* ⭐ Estrellas / Seguidores */}
          <div className="flex flex-wrap justify-center items-center gap-3 mt-4">
            <div className="flex items-center gap-1">
              <FaStar className={`w-5 h-5 ${hasStar ? "text-yellow-300" : "text-gray-200"}`} />
              <span className="text-yellow-800 dark:text-yellow-200 font-semibold">{stars.length}</span>
            </div>

            {currentUser?.uid !== user.uid && (
              <button
                onClick={toggleStar}
                disabled={processingStar}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-700 dark:hover:bg-yellow-800 text-white transition whitespace-nowrap"
              >
                {hasStar ? "Quitar Estrella" : "Dar Estrella"}
              </button>
            )}
          </div>

          {/* Botones externos y mensaje */}
          <div className="flex flex-wrap justify-center items-center gap-4 mt-6 w-full">
            {user.externalLinks && user.externalLinks.trim() !== "" && (
              <a
                href={user.externalLinks}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-2 bg-yellow-500 hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-700 text-white font-semibold rounded-lg shadow-md transition-colors duration-300 text-center whitespace-nowrap"
              >
                Visitar sitio externo
              </a>
            )}

          <button
            type="button"
            className="inline-flex items-center gap-2 px-6 py-2 bg-yellow-500 hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-700 text-white font-semibold rounded-lg shadow-md transition-all duration-300 text-center whitespace-nowrap"
            aria-label="Enviar mensaje"
            onClick={() => setOpen(true)}
          >
            <span className="text-lg">✉️</span>
            Enviar mensaje
          </button>

          <FirstMessageToPromotionalModal
            open={open}
            onClose={() => setOpen(false)}
            promoProfileId={promoProfileId}     // UID de la cuenta destino
            promoProfileName={user.name}        // Nombre visible de la cuenta
          />
          </div>
        </div>

          <hr className="my-4 border-yellow-300 dark:border-yellow-700" />

          <div className="mt-4 max-h-96 overflow-auto w-full">
            <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-100 mb-2">
              Publicaciones destacadas
            </h3>
            {/* Aquí pasamos el promoProfileId dinámico */}
            <ContentPostsPromProfiles promoProfileId={promoProfileId} />
          </div>
        </div>
      </div>
    </>
  );
}

export default PromotionalPublicProfileModal;
